import ModifiedDropdown from '@wandb/weave/common/components/elements/ModifiedDropdown';
import React, {useCallback, useMemo} from 'react';

import {type RunHistoryKeyInfoKeys} from '../../../types/run';
import {observeMetricSelectionPainWindow} from '../../../util/datadog/criticalWorkflows/painWindow/metricSelection';
import {createLazyPickerSearch} from '../../../util/pickerSearchIndex';
import {X_AXIS_LABELS} from '../defaults';
import {type XAxisChangeHandler} from './AxisSections';

type XAxisDropdownOption = {
  icon: string;
  text: string;
  value: string;
  key: string;
};

const CALENDAR_X_AXIS_KEYS = new Set([
  '_runtime',
  '_timestamp',
  '_absolute_runtime',
]);
const NOT_MONOTONIC_SUFFIX = ' (Not Monotonically Increasing)';

function isNotMonotonicXAxis(keyInfoKeys: RunHistoryKeyInfoKeys, key: string) {
  return keyInfoKeys[key] != null && !keyInfoKeys[key].monotonic;
}

/** Display text for one x-axis choice. Exported for tests. */
export function getXAxisText(
  key: string,
  keyInfoKeys: RunHistoryKeyInfoKeys,
  fromV2: boolean
): string {
  let text = X_AXIS_LABELS[key] || key;
  if (isNotMonotonicXAxis(keyInfoKeys, key) && !fromV2) {
    text += NOT_MONOTONIC_SUFFIX;
  }
  return text;
}

/** Dropdown option for one x-axis choice. Exported for tests. */
export function getXAxisOption(
  key: string,
  keyInfoKeys: RunHistoryKeyInfoKeys,
  fromV2: boolean
): XAxisDropdownOption {
  const notMonotonic = isNotMonotonicXAxis(keyInfoKeys, key);
  const icon = CALENDAR_X_AXIS_KEYS.has(key)
    ? 'calendar'
    : notMonotonic
      ? 'chart bar'
      : 'arrow up';
  return {icon, text: getXAxisText(key, keyInfoKeys, fromV2), value: key, key};
}

/**
 * Searchable display texts, one per choice, in choice order. Built once per
 * key set (lazily, on the first search) and scanned by `pickerSearchIndex`;
 * option objects are only built for the returned matches. Exported for tests.
 */
export function buildXAxisSearchTexts(
  xAxisChoices: readonly string[],
  keyInfoKeys: RunHistoryKeyInfoKeys,
  fromV2: boolean
): string[] {
  return xAxisChoices.map(choice => getXAxisText(choice, keyInfoKeys, fromV2));
}

function getVisibleXAxisChoices({
  xAxisChoices,
  selectedXAxis,
  resultLimit,
}: {
  xAxisChoices: string[];
  selectedXAxis: string | undefined;
  resultLimit: number;
}) {
  const visibleChoices = xAxisChoices.slice(0, resultLimit + 1);
  if (selectedXAxis == null || visibleChoices.includes(selectedXAxis)) {
    return visibleChoices;
  }
  return [selectedXAxis, ...visibleChoices];
}

function getXAxisOptions(
  xAxisChoices: string[],
  keyInfoKeys: RunHistoryKeyInfoKeys,
  fromV2: boolean
) {
  return xAxisChoices.map(choice =>
    getXAxisOption(choice, keyInfoKeys, fromV2)
  );
}

type HistoryKeysXAxisPickerProps = {
  keyInfoKeys: RunHistoryKeyInfoKeys;
  xAxisChoices: string[];
  fromV2: boolean;
  xAxis: string;
  resultLimit: number;
  style?: React.CSSProperties;
  onChange: XAxisChangeHandler;
};

/**
 * History-keys-backed x-axis picker. Renders the `ModifiedDropdown`
 * from options built off the full history-key set
 */
export const HistoryKeysXAxisPicker = ({
  keyInfoKeys,
  xAxisChoices,
  fromV2,
  xAxis,
  resultLimit,
  style,
  onChange,
}: HistoryKeysXAxisPickerProps) => {
  const visibleXAxisChoices = useMemo(
    () =>
      getVisibleXAxisChoices({xAxisChoices, selectedXAxis: xAxis, resultLimit}),
    [xAxisChoices, xAxis, resultLimit]
  );
  const xAxisOptions = useMemo(
    () => getXAxisOptions(visibleXAxisChoices, keyInfoKeys, fromV2),
    [fromV2, keyInfoKeys, visibleXAxisChoices]
  );
  // `ModifiedDropdown` runs the search callback over the full history-key set
  // on every debounced search (400ms trailing — once per typing pause).
  const pickerSearch = useMemo(
    function makeXAxisPickerSearch() {
      return createLazyPickerSearch(function buildXAxisTexts() {
        return buildXAxisSearchTexts(xAxisChoices, keyInfoKeys, fromV2);
      });
    },
    [fromV2, keyInfoKeys, xAxisChoices]
  );
  const searchXAxisOptions = useCallback(
    (_options: any[], query: string) =>
      pickerSearch
        .search(query, resultLimit)
        .map(i => getXAxisOption(xAxisChoices[i], keyInfoKeys, fromV2)),
    [fromV2, keyInfoKeys, pickerSearch, resultLimit, xAxisChoices]
  );
  const handleChange = useCallback<XAxisChangeHandler>(
    (event, data) => {
      if (typeof data.value === 'string') {
        observeMetricSelectionPainWindow({
          axis: 'x',
          event,
          initialSelection: [xAxis],
          nextSelection: [data.value],
          pickerVariant: 'history_keys',
        });
      }
      onChange(event, data);
    },
    [onChange, xAxis]
  );

  return (
    <ModifiedDropdown
      allowRegexSearch
      lazyLoad
      options={xAxisOptions}
      placeholder="X Axis"
      resultLimit={resultLimit}
      search={searchXAxisOptions}
      selection
      style={style}
      value={xAxis}
      onChange={handleChange}
    />
  );
};
